import React, { useState, useEffect } from 'react';
import {
  Card, Button, Form, Col, Select, Input, Row, message, Modal,
} from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { shape, string } from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { differenceBy } from 'lodash';
import AddCohortModal from './AddModal';
import { GET_ALL_COHORT, WITHDRAW_FROM_COHORT } from '../../../../../graphql';

const { Item: FormItem } = Form;
const { Option } = Select;
const { confirm } = Modal;
const CohortCard = ({ user, title = 'Teacher Cohort and Subjects' }) => {
  const { data, loading } = useQuery(GET_ALL_COHORT);
  const [withdrawFromCohort] = useMutation(WITHDRAW_FROM_COHORT);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allCohort, setAllCohort] = useState([]);
  useEffect(() => {
    if (!loading && data) {
      setAllCohort(data.cohorts);
    }
  }, [data, loading]);
  const getCohortSubjects = (cohortId, subjects) => subjects
    .filter((subject) => subject.cohortId === cohortId);
  /** The cohorts this user is not already in. */
  const selectCohort = (enrolled) => differenceBy(allCohort, enrolled, 'id');
  /**
   * Withdrawal takes the cohort's subjects with it, so there is no way to
   * leave somebody enrolled in subjects for a class they have left.
   */
  const handleRemoveCohort = async (cohortId) => {
    const hide = message.loading('Removing cohort..', 0);
    try {
      await withdrawFromCohort({ variables: { userId: user.id, cohortId } });
      message.success('Cohort Removed');
    } catch (error) {
      message.error(error.message || 'Error, could not remove cohort');
    } finally {
      hide();
    }
  };

  const showConfirm = (cohortId) => {
    confirm({
      title: 'Do you wish to remove this cohort',
      icon: <ExclamationCircleOutlined />,
      content: 'this would prevent user from access cohort resource',
      onOk() {
        return handleRemoveCohort(cohortId);
      },
      onCancel() {},
    });
  };

  return (
    <>
      {showAddModal && (
      <AddCohortModal
        cohorts={selectCohort(user.cohorts)}
        userId={user.id}
        visible={showAddModal}
        closeModal={setShowAddModal}
      />
      )}
      <Card
        className="cohort-card"
        title={title}
        extra={(
          <Button
            className="gx-m-0"
            type="primary"
            size="small"
            onClick={() => setShowAddModal(true)}
          >
            Add Cohort
          </Button>
        )}
      >
        <Form layout="vertical">
          {user.cohorts.map(({ name, id: cohortId }, idx) => {
            const cohortSubjects = getCohortSubjects(cohortId, user.subjects);
            return (
              <Row key={cohortId} className="no-flex-column">
                <Col xs={24} sm={10}>
                  <FormItem initialValue={name} name={`cohort_${cohortId}`} label={idx === 0 ? 'Cohort' : ''}>
                    <Input disabled />
                  </FormItem>
                </Col>
                <Col xs={24} sm={10}>
                  <FormItem initialValue={cohortSubjects.map(({ id: _id }) => _id)} name={`subject_${cohortId}`} label={idx === 0 ? 'Subjects' : ''}>
                    <Select disabled mode="multiple">
                      {cohortSubjects.map(({ id, name: _name }) => (
                        <Option key={id} value={id}>{_name}</Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
                <Col className={`${idx === 0 && 'gx-mt-4'}`} xs={12} sm={4}>
                  <Button onClick={() => showConfirm(cohortId)} type="text" className="gx-m-0">
                    <DeleteOutlined />
                  </Button>
                </Col>
              </Row>
            );
          })}
        </Form>
      </Card>
    </>
  );
};

CohortCard.propTypes = {
  user: shape({}).isRequired,
  title: string,
};

export default CohortCard;
